import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, X, ShieldCheck } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import { PIN_BYPASS_ENABLED } from "../../lib/pinConfig";

/**
 * Feb 2026 · 4-stellige PIN-Sicherheitsabfrage vor dem Öffnen sensibler
 * Editor-Dialoge (aktuell: Kundenprofil bearbeiten).
 *
 * Ruft `POST /team/settings/edit-pin/verify` mit der eingegebenen PIN und
 * ruft bei Erfolg `onSuccess()` auf. Bei falscher PIN bleibt der Dialog
 * offen und zeigt eine Inline-Fehlermeldung; ein Toast informiert den
 * Admin zusätzlich (visuell + haptisch).
 *
 * Bedienung:
 *   - 4 Ziffern-Slots mit Auto-Focus zum nächsten Feld
 *   - Backspace springt rückwärts
 *   - Enter/Return submittet
 *   - Escape schließt
 *
 * Der Werksstandard ist `0611` — solange kein Admin die PIN in
 * `/team/settings/edit-pin` geändert hat, ist dieser Wert gültig.
 */
export function AdminPinPromptDialog({ title, description, purpose, onClose, onSuccess }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const inputs = useRef([]);

  // Feb 2026 · 10 Feb 2026 · TEMPORÄRER PIN-BYPASS (siehe lib/pinConfig.js).
  // Wenn aktiviert: Modal überhaupt nicht rendern, direkt `onSuccess` feuern.
  // Nach Deployment: PIN_BYPASS_ENABLED = false setzen, Verhalten kehrt
  // unverändert zur klassischen 4-Ziffern-Verifikation zurück.
  useEffect(() => {
    if (PIN_BYPASS_ENABLED) {
      onSuccess?.();
    }
  }, [onSuccess]);

  useEffect(() => {
    if (PIN_BYPASS_ENABLED) return;
    // Fokus auf das erste Feld beim Öffnen — spart einen Klick.
    setTimeout(() => inputs.current[0]?.focus(), 20);
  }, []);

  useEffect(() => {
    if (PIN_BYPASS_ENABLED) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (PIN_BYPASS_ENABLED) return null;

  const setAt = (idx, val) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 1);
    setDigits((d) => {
      const next = [...d];
      next[idx] = cleaned;
      return next;
    });
    if (cleaned && idx < 3) inputs.current[idx + 1]?.focus();
    setError("");
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    } else if (e.key === "Enter") {
      submit();
    }
  };

  const onPaste = (e) => {
    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!t) return;
    e.preventDefault();
    const arr = ["", "", "", ""];
    for (let i = 0; i < t.length; i++) arr[i] = t[i];
    setDigits(arr);
    setError("");
    inputs.current[Math.min(t.length, 3)]?.focus();
  };

  const submit = async () => {
    const pin = digits.join("");
    if (pin.length !== 4) {
      setError("Bitte 4 Ziffern eingeben.");
      return;
    }
    setChecking(true);
    try {
      // Feb 2026 · 9 Sep 2026 · `purpose` durchreichen für PIN-Audit-Log-Filter.
      // Default bleibt "sensitive-edit" (siehe admin_pin.py); ein expliziter
      // Wert wie "project-edit" landet als Purpose-Zeile im Audit-Log und
      // wird auf `/team/settings/pin-audit` als eigenständige Kategorie
      // gefiltert.
      const body = purpose ? { pin, purpose } : { pin };
      await api.post("/team/settings/edit-pin/verify", body);
      onSuccess?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setError("PIN ist falsch. Bitte erneut versuchen.");
        setDigits(["", "", "", ""]);
        inputs.current[0]?.focus();
      } else {
        setError(formatErr(err?.response?.data?.detail) || "Prüfung fehlgeschlagen.");
      }
    } finally {
      setChecking(false);
    }
  };

  const canSubmit = digits.every((d) => d.length === 1) && !checking;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-navy/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="admin-pin-prompt"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-sm bg-white shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
          <div className="inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
            <h2 className="font-display text-lg text-navy">
              {title || "Sicherheitsabfrage"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-pin-prompt-close"
            aria-label="Schließen"
            className="text-navy-dark/50 hover:text-navy p-1"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="px-5 py-6">
          <p className="text-[13px] text-navy-dark/75 mb-4 leading-snug">
            {description || "Bitte den 4-stelligen PIN eingeben, um fortzufahren."}
          </p>

          <div
            className="flex items-center justify-center gap-2 mb-3"
            onPaste={onPaste}
          >
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={(el) => { inputs.current[idx] = el; }}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={d}
                maxLength={1}
                onChange={(e) => setAt(idx, e.target.value)}
                onKeyDown={(e) => onKeyDown(idx, e)}
                data-testid={`admin-pin-digit-${idx}`}
                className="w-12 h-14 text-center text-xl border border-navy/25 focus:border-gold focus:outline-none bg-white font-mono"
                aria-label={`Ziffer ${idx + 1}`}
              />
            ))}
          </div>

          {error && (
            <div
              className="text-[12px] text-center py-1.5"
              style={{ color: "#B02A37" }}
              data-testid="admin-pin-error"
            >
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              data-testid="admin-pin-cancel"
              className="flex-1 px-4 py-2 text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy border border-navy/15"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="admin-pin-submit"
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-4 py-2 text-[12px] tracking-wider uppercase"
              style={{ background: BRAND_COLORS.navy }}
            >
              {checking ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
              )}
              Bestätigen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
